import { useState, useCallback } from 'react';
import { Share, Platform } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useCommunity } from './useCommunity';
import { useToastMessages } from './useToastMessages';

interface ShareOptions {
  type: 'post' | 'profile' | 'event';
  id?: number;
  userName?: string;
  title?: string;
}

export function useShare() {
  const { t } = useTranslation();
  const community = useCommunity();
  const { showSuccessToast, showErrorToast } = useToastMessages();
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareUrl, setShareUrl] = useState('');

  const generateUrl = useCallback((options: ShareOptions) => {
    const base = Platform.OS === 'web' && typeof window !== 'undefined'
      ? window.location.origin
      : `https://app.${community?.domain}`;

    if (options.type === 'post') return `${base}/post/${options.id}`;
    if (options.type === 'event') return `${base}/events/${options.userName}`;
    return `${base}/@/${options.userName}`;
  }, [community?.domain]);

  const handleShare = useCallback(async (options: ShareOptions) => {
    try {
      const url = generateUrl(options);
      
      let shareTitle: string;
      let shareMessage: string;

      if (options.type === 'post') {
        shareTitle = t('post.share.title');
        shareMessage = t('post.share.message', {
          userName: options.userName,
          postId: options.id,
        });
      } else if (options.type === 'event') {
        shareTitle = t('events.share.title', 'Check out this event');
        shareMessage = t('events.share.message', {
          eventName: options.title,
        });
      } else {
        shareTitle = t('profile.share.title');
        shareMessage = t('profile.share.message', {
          userName: options.userName,
        });
      }

      if (Platform.OS === 'web') {
        setShareUrl(url);
        setShowShareModal(true);
      } else {
        const result = await Share.share({
          title: shareTitle,
          message: Platform.OS === 'ios' ? shareMessage : `${shareMessage}\n${url}`,
          url: url,
        });

        if (result.action === Share.sharedAction) {
          showSuccessToast(t('common.share.success'));
        }
      }
    } catch (error) {
      showErrorToast(t('common.share.error'), error);
    }
  }, [generateUrl, t, showSuccessToast, showErrorToast]);

  const handleCopyUrl = useCallback(async () => {
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(shareUrl);
        showSuccessToast(t('common.share.copied'));
      }
    } catch (error) {
      showErrorToast(t('common.share.error'), error);
    }
  }, [shareUrl, showSuccessToast, showErrorToast, t]);

  const handleOpenUrl = useCallback(() => {
    try {
      window.open(shareUrl, '_blank');
    } catch (error) {
      showErrorToast(t('common.share.error'), error);
    }
  }, [shareUrl, showErrorToast, t]);

  return {
    handleShare,
    handleCopyUrl,
    handleOpenUrl,
    showShareModal,
    setShowShareModal,
    shareUrl,
  };
} 